'use client';

import type { AdminV2EntryAction } from '@/admin-v2/entry-actions/entry-action';
import {
  CMS_SHELL_PREVIEW_AUTHENTICATED_CREDITS,
  type CmsShellViewerMode,
} from '@/cms/chrome/shell-viewer-mode';
import {
  CmsAccountMenu,
  useCmsAccountLocalCopy,
} from '@/cms/components/chrome/cms-account-menu';
import { useCmsAccountSettingsDialog } from '@/cms/components/chrome/cms-account-settings-state';
import { CmsPrimaryCtaTrigger } from '@/cms/components/chrome/cms-primary-cta-trigger';
import { CmsRemainingCreditsSlot } from '@/cms/components/chrome/cms-remaining-credits-slot';
import { CmsShellViewerState } from '@/cms/components/chrome/cms-shell-viewer-state';
import type { CmsWorkspaceLaunchContext } from '@/cms/workspace/launch-context';
import { adminV2DefaultLoginPopupSceneSlug } from '@/site-runtime/code-owned/login-popup-scenes';
import {
  SiteRuntimeActionTrigger,
  type SiteRuntimeElementDataAttributes,
  SiteRuntimeTargetTrigger,
} from '@/site-runtime/components/site-runtime-entry-action-controller';
import type { SiteRuntimeTarget } from '@/site-runtime/runtime-config-contract';
import type { ReactNode } from 'react';
import styles from './cms-site-header.module.css';

export type CmsSiteHeaderActionMode = 'cms' | 'runtime';

function CmsHeaderGuestActions({
  authHintSensitive,
  hasWorkspaceTarget,
  actionMode,
  accountLoginDataAttributes,
  accountPrimaryCtaDataAttributes,
  primaryCtaLabel,
  primaryCtaTarget,
  utilityLabel,
  workspaceLaunchContext,
}: {
  actionMode: CmsSiteHeaderActionMode;
  authHintSensitive: boolean;
  accountLoginDataAttributes?: SiteRuntimeElementDataAttributes;
  accountPrimaryCtaDataAttributes?: SiteRuntimeElementDataAttributes;
  hasWorkspaceTarget: boolean;
  primaryCtaLabel?: string;
  primaryCtaTarget?: SiteRuntimeTarget;
  utilityLabel?: string;
  workspaceLaunchContext?: CmsWorkspaceLaunchContext;
}) {
  let guestActionContent: ReactNode;

  if (actionMode === 'runtime') {
    const loginAction: AdminV2EntryAction = {
      type: 'open_popup',
      popupId: 'login',
      branchSlug: adminV2DefaultLoginPopupSceneSlug,
    };

    if (primaryCtaLabel && !primaryCtaTarget) {
      throw new Error(
        'Runtime header primary CTA target is required when its label is configured.'
      );
    }

    guestActionContent = (
      <>
        {utilityLabel ? (
          <SiteRuntimeActionTrigger
            action={loginAction}
            className={`cms-button-text ${styles.utilityButton}`}
            dataAttributes={accountLoginDataAttributes}
          >
            {utilityLabel}
          </SiteRuntimeActionTrigger>
        ) : null}
        {primaryCtaLabel && primaryCtaTarget ? (
          <SiteRuntimeTargetTrigger
            className={`cms-button-primary cms-button-sm ${styles.primaryCta}`}
            dataAttributes={accountPrimaryCtaDataAttributes}
            target={primaryCtaTarget}
          >
            {primaryCtaLabel}
          </SiteRuntimeTargetTrigger>
        ) : null}
      </>
    );
  } else {
    guestActionContent =
      primaryCtaLabel && hasWorkspaceTarget ? (
        <CmsPrimaryCtaTrigger
          className={`cms-button-primary cms-button-sm ${styles.primaryCta}`}
          dataAttributes={accountPrimaryCtaDataAttributes}
          workspaceLaunchContext={workspaceLaunchContext}
        >
          {primaryCtaLabel}
        </CmsPrimaryCtaTrigger>
      ) : null;
  }

  return (
    <>
      <span
        className={styles.guestActions}
        data-auth-hint-sensitive={authHintSensitive ? 'true' : undefined}
        data-auth-island-state="guest"
      >
        {guestActionContent}
      </span>
      {authHintSensitive ? <CmsHeaderAccountSkeleton bootstrapOnly /> : null}
    </>
  );
}

function CmsHeaderAccountSkeleton({
  bootstrapOnly = false,
}: {
  bootstrapOnly?: boolean;
}) {
  return (
    <span
      aria-hidden="true"
      className={
        bootstrapOnly
          ? `${styles.accountSkeleton} ${styles.accountSkeletonBootstrap}`
          : styles.accountSkeleton
      }
      data-auth-bootstrap-skeleton={bootstrapOnly ? '' : undefined}
      data-auth-island-state={bootstrapOnly ? undefined : 'loading'}
    >
      <span
        className={`${styles.accountSkeletonBlock} ${styles.accountSkeletonBalance}`}
      />
      <span
        className={`${styles.accountSkeletonBlock} ${styles.accountSkeletonAvatar}`}
      />
    </span>
  );
}

function CmsHeaderAuthenticatedActions({
  currentUser,
  locale,
  viewerMode,
}: {
  currentUser: Parameters<typeof CmsAccountMenu>[0]['user'];
  locale: string;
  viewerMode: CmsShellViewerMode;
}) {
  const accountCopy = useCmsAccountLocalCopy();
  const settingsDialog = useCmsAccountSettingsDialog();

  return (
    <>
      <CmsRemainingCreditsSlot
        variant="header"
        label={accountCopy.creditsLabel}
        onOpenCredits={() => settingsDialog.openSettings('credits')}
        previewState={
          viewerMode === 'authenticated'
            ? {
                authenticated: true,
                availableCredits: CMS_SHELL_PREVIEW_AUTHENTICATED_CREDITS,
              }
            : undefined
        }
      />
      <CmsAccountMenu
        locale={locale}
        settingsDialog={settingsDialog}
        user={currentUser}
      />
    </>
  );
}

export function CmsSiteHeaderActions({
  accountLoginDataAttributes,
  accountPrimaryCtaDataAttributes,
  actionMode,
  hasWorkspaceTarget,
  locale,
  primaryCtaLabel,
  primaryCtaTarget,
  utilityLabel,
  viewerMode = 'live',
  workspaceLaunchContext,
  children,
}: {
  accountLoginDataAttributes?: SiteRuntimeElementDataAttributes;
  accountPrimaryCtaDataAttributes?: SiteRuntimeElementDataAttributes;
  actionMode: CmsSiteHeaderActionMode;
  hasWorkspaceTarget: boolean;
  locale: string;
  primaryCtaLabel?: string;
  primaryCtaTarget?: SiteRuntimeTarget;
  utilityLabel?: string;
  viewerMode?: CmsShellViewerMode;
  workspaceLaunchContext?: CmsWorkspaceLaunchContext;
  children?: (accountContent: ReactNode) => ReactNode;
}) {
  return (
    <CmsShellViewerState mode={viewerMode}>
      {({
        currentUser,
        isAuthenticated,
        isPending,
        mode,
        shouldAssumeAuthenticated,
      }) => {
        let accountContent: ReactNode = null;

        if (isAuthenticated && currentUser) {
          accountContent = (
            <CmsHeaderAuthenticatedActions
              currentUser={currentUser}
              locale={locale}
              viewerMode={mode}
            />
          );
        } else if (isPending || shouldAssumeAuthenticated) {
          accountContent = <CmsHeaderAccountSkeleton />;
        } else {
          accountContent = (
            <CmsHeaderGuestActions
              actionMode={actionMode}
              authHintSensitive={mode === 'live'}
              accountLoginDataAttributes={accountLoginDataAttributes}
              accountPrimaryCtaDataAttributes={accountPrimaryCtaDataAttributes}
              hasWorkspaceTarget={hasWorkspaceTarget}
              primaryCtaLabel={primaryCtaLabel}
              primaryCtaTarget={primaryCtaTarget}
              utilityLabel={utilityLabel}
              workspaceLaunchContext={workspaceLaunchContext}
            />
          );
        }

        return children ? children(accountContent) : accountContent;
      }}
    </CmsShellViewerState>
  );
}
